import type { ClientSearchSortOrder } from "../store/schema/SearchTableQuery";
import { createNameCollator } from "./sortRowsByName";

/**
 * Finds the row to jump to in rows already sorted by display name (see sortRowsByName).
 *
 * Lands on the first name that starts with the search text, so 'mar' finds the first "Mar..."
 * name in either direction. When no name starts with it, A to Z lands on the first name after
 * the text and Z to A on the first name before it. Rows without a name are skipped.
 *
 * @returns the index of the row to jump to; the last named row when no name matches,
 * or -1 when there are no named rows or the search text is blank
 */
export default function findNameJumpToIndex<T>(rows: readonly T[], getName: (row: T) => string | undefined, sortOrder: ClientSearchSortOrder, searchText: string): number {
    const target = searchText.trim();
    if (rows.length === 0 || !target) {
        return -1;
    }
    
    const collator = createNameCollator();
    const isDescending = sortOrder === "SenderDescending" || sortOrder === "RecipientDescending";
    const names = rows.map(row => getName(row)?.trim());
    
    const prefixMatchIndex = names.findIndex(name => !!name && collator.compare(name.slice(0, target.length), target) === 0);
    if (prefixMatchIndex >= 0) {
        return prefixMatchIndex;
    }
    
    // Compare full names so the numeric collation orders 'Team 9' before 'Team 10'.
    const passedTargetIndex = names.findIndex(name => {
        if (!name) {
            return false;
        }
        const comparison = collator.compare(name, target);
        return isDescending ? comparison <= 0 : comparison >= 0;
    });
    
    if (passedTargetIndex >= 0) {
        return passedTargetIndex;
    }
    
    for (let index = names.length - 1; index >= 0; index--) {
        if (names[index]) {
            return index;
        }
    }
    
    return -1;
}
